import React, { useState, useRef, useEffect } from 'react';
import * as Icons from 'lucide-react';

/**
 * SearchableDropdown Component
 * 
 * A reusable dropdown component with search functionality
 * Replaces plain text search inputs across the application
 * 
 * @param {Array} options - Array of objects with { value, label } or array of strings
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Optional label above dropdown
 * @param {boolean} clearable - Show clear button (default: true)
 * @param {string} className - Additional CSS classes
 */
const SearchableDropdown = React.forwardRef(({
  options = [],
  value = '',
  onChange,
  placeholder = 'Search...',
  label = '',
  clearable = true,
  className = '',
  tabIndex = 0,
  disabled = false,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const internalRef = useRef(null);
  const inputRef = useRef(null);
  
  // Use the forwarded ref or fall back to internal ref
  const dropdownRef = ref || internalRef;

  // Normalize options to { value, label } format
  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  // Filter options based on search term
  const filteredOptions = normalizedOptions.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get display value
  const selectedOption = normalizedOptions.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle option selection
  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle clear
  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (disabled) return;
    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        toggleDropdown();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          toggleDropdown();
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          setIsOpen(false);
        }
        break;
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Dropdown trigger */}
      <div
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : tabIndex}
        className={`w-full flex items-center justify-between px-3 sm:px-4 py-2 bg-white border border-gray-200 rounded-lg transition-all duration-200 min-h-[40px] ${
          disabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : isOpen
            ? 'ring-2 ring-indigo-600 border-transparent cursor-pointer'
            : 'hover:border-gray-300 cursor-pointer'
        } ${disabled ? 'opacity-50' : ''}`}
        title={displayValue || placeholder}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icons.Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span
            className={`text-sm truncate ${
              displayValue ? 'text-gray-900' : 'text-gray-500'
            }`}
          >
            {displayValue || placeholder}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {clearable && value && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              type="button"
            >
              <Icons.X className="w-4 h-4 text-gray-500" />
            </button>
          )}
          <Icons.ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>

      {/* Dropdown panel - PROMPT-137: Fixed scroll container */}
      {isOpen && (
        <div className="absolute z-[9999] w-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[320px] flex flex-col overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 flex-shrink-0">
            <div className="relative ml-2">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className="w-full pl-4 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
            </div>
          </div>

          {/* PROMPT-137: Options list with proper scrolling support */}
          <div 
            className="overflow-y-auto overscroll-contain flex-1"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y'
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    option.value === value
                      ? 'bg-indigo-50 text-indigo-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  type="button"
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

SearchableDropdown.displayName = 'SearchableDropdown';

export default SearchableDropdown;
